/**
 * Lifestyle quiz — Figma Quizzes › `Lifestyle quiz · desktop` 3229:105744 / `mobile`
 * 3229:106519. Welcome → Occupation (Other opens a text field, R171) → Dress code
 * (skipped for At home and Study) → Week (multi) → Events and trips → Climate → Note →
 * Result. Every question is optional and applied at once (R203); no AI mark.
 *
 * The questions are the designer's set; the server's `default-lifestyle-survey`
 * definition still has the older one (BE-152). Section ids and values reuse the
 * server's where they mean the same (`work_dress_code`, `climate`, `note`).
 */
import type { SurveyResponseAnswer, SurveyResponseBody, UserLifestyle } from '@/features/survey/api/surveys'

export type LifestyleQuizQuestionId = 'occupation' | 'work_dress_code' | 'week' | 'events_trips' | 'climate'

export interface LifestyleQuizQuestion {
  id: LifestyleQuizQuestionId
  multi: boolean
  options: readonly { value: string; icon?: string }[]
  /** The option that opens a text field (R171). */
  customValue?: string
}

export const LIFESTYLE_QUIZ_QUESTIONS: readonly LifestyleQuizQuestion[] = [
  {
    id: 'occupation',
    multi: false,
    options: [
      { value: 'office', icon: 'office' },
      { value: 'remote', icon: 'remote-work' },
      { value: 'study', icon: 'study' },
      { value: 'creative', icon: 'creative' },
      { value: 'home', icon: 'home' },
      { value: 'other', icon: 'edit' },
    ],
    customValue: 'other',
  },
  {
    id: 'work_dress_code',
    multi: false,
    options: [
      { value: 'formal' },
      { value: 'business_casual' },
      { value: 'smart_casual' },
      { value: 'casual' },
      { value: 'no_code' },
    ],
  },
  {
    id: 'week',
    multi: true,
    options: [
      { value: 'office' },
      { value: 'sport' },
      { value: 'evenings' },
      { value: 'travel' },
      { value: 'family' },
      { value: 'home' },
    ],
  },
  {
    id: 'events_trips',
    multi: false,
    options: [{ value: 'rarely' }, { value: 'few_year' }, { value: 'monthly' }, { value: 'weekly' }],
  },
  {
    id: 'climate',
    multi: false,
    options: [{ value: 'cold' }, { value: 'mild' }, { value: 'warm' }, { value: 'hot' }, { value: 'mixed' }],
  },
]

export const LIFESTYLE_QUIZ_NOTE_MAX = 2000

/** Dress code is skipped for At home and Study (03 note). */
const NO_DRESS_CODE = new Set(['home', 'study'])

export type LifestyleQuizStep = 'welcome' | LifestyleQuizQuestionId | 'note' | 'result'

export interface LifestyleQuizState {
  /** single: [value]; multi: values; nothing chosen: [] */
  answers: Partial<Record<LifestyleQuizQuestionId, string[]>>
  /** Text of Other (occupation). */
  custom: string
  note: string
}

export function emptyLifestyleQuizState(): LifestyleQuizState {
  return { answers: {}, custom: '', note: '' }
}

export function lifestyleQuizSteps(state: LifestyleQuizState): LifestyleQuizStep[] {
  const occupation = state.answers.occupation?.[0]
  const steps: LifestyleQuizStep[] = ['welcome']
  for (const question of LIFESTYLE_QUIZ_QUESTIONS) {
    if (question.id === 'work_dress_code' && occupation && NO_DRESS_CODE.has(occupation)) continue
    steps.push(question.id)
  }
  steps.push('note', 'result')
  return steps
}

/** Single choice replaces (tapping the chosen one clears it); multi toggles. */
export function toggleLifestyleQuizAnswer(current: string[] | undefined, value: string, multi: boolean): string[] {
  const chosen = current ?? []
  if (multi) return chosen.includes(value) ? chosen.filter((item) => item !== value) : [...chosen, value]
  return chosen[0] === value ? [] : [value]
}

export function lifestyleQuizProgress(steps: LifestyleQuizStep[], step: LifestyleQuizStep): number {
  const index = steps.indexOf(step)
  if (index <= 0) return 0
  return Math.min(1, index / (steps.length - 1))
}

/** `PUT /surveys/{key}/response` — every answer under its section id; the note as free text. */
export function lifestyleQuizResponseBody(state: LifestyleQuizState, steps: LifestyleQuizStep[]): SurveyResponseBody {
  const answers: Record<string, SurveyResponseAnswer> = {}
  for (const question of LIFESTYLE_QUIZ_QUESTIONS) {
    if (!steps.includes(question.id)) continue
    const chosen = state.answers[question.id] ?? []
    if (!chosen.length) continue
    if (question.multi) {
      answers[question.id] = { value: chosen, isCustom: false, multiAnswer: true }
      continue
    }
    const custom = question.customValue === chosen[0] ? state.custom.trim() : ''
    answers[question.id] = custom
      ? { value: null, isCustom: true, multiAnswer: false, customText: custom }
      : { value: chosen[0], isCustom: false, multiAnswer: false }
  }
  const note = state.note.trim().slice(0, LIFESTYLE_QUIZ_NOTE_MAX)
  if (note) answers.note = { value: null, isCustom: true, multiAnswer: false, customText: note }
  return { answers, freeText: note || null }
}

/** What the server stored, as the quiz's state (questions it does not know are ignored). */
export function lifestyleQuizStateFrom(lifestyle: UserLifestyle | null | undefined): LifestyleQuizState {
  const state = emptyLifestyleQuizState()
  const known = new Map(LIFESTYLE_QUIZ_QUESTIONS.map((question) => [question.id as string, question]))
  for (const answer of lifestyle?.answers ?? []) {
    const question = known.get(answer.questionId)
    if (!question || !answer.answerKey) continue
    if (!question.options.some((option) => option.value === answer.answerKey)) continue
    const id = question.id
    state.answers[id] = question.multi ? [...(state.answers[id] ?? []), answer.answerKey] : [answer.answerKey]
  }
  state.note = lifestyle?.note ?? ''
  return state
}
